import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { EMAIL_CENTER_ACTIONS } from '@/constants/actions';
import PERMISSIONS from '@/constants/permissions';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import {
  createTemplateFragmentRequest,
  updateTemplateFragmentRequest,
} from '@/services/api/emailCenter';
import {
  PartnerTranslationModel,
  TemplateFragmentModel,
} from '@/typings/api/emailCenter';
import { normalizeApiError } from '@/utils/apiErrors';
import { zIndexAboveDrawers } from '@/utils/dom';
import {
  buildEmailConfigPreviewContext,
  buildThemePreviewContext,
} from '@/utils/emailCenter';
import { useHasAccess } from '@/utils/hooks';
import { F, getIntlTranslation } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  HighlightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useIntl, useSelector } from 'umi';
import PreviewPane from '../../components/EmailBodyEditor/PreviewPane';
import TestDataPanel from '../../components/EmailBodyEditor/TestDataPanel';
import TwigCodeEditor, {
  EditorSelection,
  TwigCodeEditorHandle,
} from '../../components/EmailBodyEditor/TwigCodeEditor';
import {
  extractFragmentReferences,
  extractTransKeys,
} from '../../components/EmailBodyEditor/insertion';
import {
  fragmentDisplayName,
  humanizeTransKey,
} from '../../components/EmailBodyEditor/labels';
import { useLastGoodRender } from '../../components/EmailBodyEditor/lastGoodRender';
import { renderTwigPreview } from '../../components/EmailBodyEditor/twigRenderer';
import { resolveVariableLabel } from '../../components/EmailBodyEditor/visual/labels';
import {
  MANIFEST_WARNING,
  parseManifest,
} from '../../components/EmailBodyEditor/visual/manifest';
import {
  isRefusal,
  planFieldFromSelection,
  withFieldFromSelection,
} from '../../components/EmailBodyEditor/visual/manifestAuthoring';
import { inferManifest } from '../../components/EmailBodyEditor/visual/manifestInference';
import { LANGUAGE_CODES } from '../../constants';
import { resolveTranslations } from '../../translationCatalog';
import { useJourneyTheme } from '../../useJourneyTheme';
import { useSaveTranslation } from '../../useSaveTranslation';
import { hasUnsavedFragmentChanges } from './fragmentDraftDirty';
import {
  FragmentDrawerMode,
  resolveFragmentDrawerMode,
} from './fragmentDrawerMode';
import {
  buildFragmentPreviewSource,
  buildPreviewFragments,
  DRAFT_FRAGMENT_KEY,
} from './fragmentPreview';
import { layoutProblemMessage } from './layoutProblemMessages';
import {
  blockingLayoutErrors,
  LAYOUT_STARTER,
  layoutErrors,
  validateLayoutSource,
} from './layoutValidation';

import { useEmailCenterValidateMessages } from '../../useValidateMessages';
import EditableContentPanel from './EditableContentPanel';
import styles from './FragmentsSection.less';
import { useFragmentDeletion } from './useFragmentDeletion';

interface FragmentEditorDrawerProps {
  /**
   * The requested mode. A record the partner does not own can only ever be
   * copied, so `edit` resolves to `clone` for one — see
   * {@link resolveFragmentDrawerMode}.
   */
  mode: FragmentDrawerMode;
  record: TemplateFragmentModel | null;
  /**
   * The type to create — fixed by the card the drawer was opened from (Layout
   * vs Building block); ignored when editing an existing fragment.
   */
  presetType?: 'layout' | 'partial';
  /** Full fragment catalog (for the preview-wrap picker and reference open). */
  fragments: TemplateFragmentModel[];
  /** Fragment key → source, for rendering the preview. */
  fragmentSources: Record<string, string>;
  /** Partner translations, for resolving `|trans` in the preview. */
  translations: PartnerTranslationModel[];
  onClose: () => void;
  /** Called after a successful save so the parent can refetch the catalog. */
  onSaved: () => void;
  /** Opens another block (Cmd/Ctrl-click a reference) — stacks a new drawer. */
  onOpenReference: (key: string) => void;
  /**
   * True when another editor drawer is open beneath this one — shows a Back
   * affordance so returning to the previous fragment is obvious.
   */
  showBack?: boolean;
}

/**
 * The create/edit/duplicate editor for a single template fragment: its name,
 * the Twig/HTML editor and a live preview with sample-data and translation
 * panels. The reference key is server-assigned and read-only here. Holds all of
 * its own draft state so several instances can stack (opening a referenced
 * block opens another drawer on top).
 */
const FragmentEditorDrawer: React.FC<FragmentEditorDrawerProps> = ({
  mode: requestedMode,
  record,
  presetType = 'partial',
  fragments,
  fragmentSources,
  translations,
  onClose,
  onSaved,
  onOpenReference,
  showBack,
}) => {
  const intl = useIntl();
  const validateMessages = useEmailCenterValidateMessages();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  // Default the preview wrap to the layout family the partial belongs to
  // (partials/xxxl/* → layout/xxxl/*), if one exists.
  const [previewLayoutKey, setPreviewLayoutKey] = useState<string | null>(() =>
    record && record.type !== 'layout'
      ? fragments.find(
          (fragment) =>
            fragment.type === 'layout' &&
            fragment.key.split('/')[1] === record.key.split('/')[1],
        )?.key ?? null
      : null,
  );
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const [transValues, setTransValues] = useState<Record<string, string>>({});
  const [savingTransKeys, setSavingTransKeys] = useState<
    Record<string, boolean>
  >({});

  /**
   * The type is fixed by the context the drawer was opened from — the layouts
   * card creates a layout, the partials card a building block, and an
   * existing fragment keeps whatever it is. Nothing about editing a layout
   * should let it become a partial (or the reverse): the key namespace, the
   * preview wrapping and every `{% extends %}` pointing at it all assume the
   * type it already has. So it is a constant here, not a form field.
   */
  const fragmentType = record ? record.type : presetType;

  // Ownership, not the caller, decides whether this is an edit or a copy.
  const mode = resolveFragmentDrawerMode(requestedMode, record);
  const isClone = mode === 'clone';

  /**
   * Seed the form once for this instance (stable across this drawer's life).
   *
   * A new layout starts from a working document rather than an empty
   * buffer: it *is* the email — doctype, html, body and a content block — and
   * an empty editor is where layouts like `<p>test</p>` come from. A building
   * block is a piece of one, so it still starts blank.
   */
  const initialValues = useRef(
    record
      ? {
          // A copy needs a name of its own from the start: it is a second block
          // beside the original, not a version of it, and two identically named
          // rows in the catalog are indistinguishable.
          name: isClone
            ? getIntlTranslation(intl, {
                id: 'pages.emailCenter.fragments.copyName',
                defaultMessage: 'Copy of {name}',
                values: { name: fragmentDisplayName(record) },
              })
            : fragmentDisplayName(record),
          content: record.content,
        }
      : {
          name: '',
          content: presetType === 'layout' ? LAYOUT_STARTER : '',
        },
  ).current;

  const watchedContent = Form.useWatch('content', form);
  const watchedName = Form.useWatch('name', form);

  /**
   * The reference key is not a field, in any mode.
   *
   * It is assigned by the server and immutable after that, because every
   * `{% include %}` and every stored design document points at it. A copy gets
   * a *new* key rather than the original's — that is what keeps it a second
   * block instead of a replacement — so, like a create, it has no key to show
   * yet and the preview registers the draft under a placeholder.
   */
  const fragmentKey = isClone ? null : record?.key ?? null;

  // Default-language partner translations so `|trans` resolves in previews.
  const transMap = useMemo(
    () => resolveTranslations(translations, LANGUAGE_CODES[0]),
    [translations],
  );

  const layoutChoices = useMemo(
    () =>
      fragments
        .filter(
          (fragment) =>
            fragment.type === 'layout' &&
            // A layout draft previews as itself — offering it as a wrapper
            // for itself (or, copying one, the original it is a copy of) would
            // just nest it.
            fragment.key !== record?.key,
        )
        .map((fragment) => ({
          value: fragment.key,
          label: fragmentDisplayName(fragment),
        })),
    [fragments, record],
  );

  const draftContent = typeof watchedContent === 'string' ? watchedContent : '';

  // `{% include %}` / `{% extends %}` references in the draft that point at a
  // fragment key the catalog doesn't have — these would fail to resolve when
  // the server renders emails, so warn while editing.
  const unknownReferences = useMemo(() => {
    // An empty map means the catalog hasn't loaded yet — validating then would
    // flag every real reference as unknown while the fetch is in flight.
    if (Object.keys(fragmentSources).length === 0) return [];
    return extractFragmentReferences(draftContent).filter(
      (key) => !(key in fragmentSources),
    );
  }, [draftContent, fragmentSources]);

  // Creating, the block has no key yet, so the preview registers the draft
  // under a placeholder.
  const draftKey = fragmentKey ?? DRAFT_FRAGMENT_KEY;
  const draftType = fragmentType === 'layout' ? 'layout' : 'partial';

  /**
   * What is wrong with this layout, live from the buffer.
   *
   * Only for layouts. A layout is the whole email document and nothing outside
   * its `{% block %}`s is reachable, so "no content block" and "markup that does
   * not nest" are not style opinions — they are an email that arrives empty or
   * that every client repairs differently. Neither is visible in the preview
   * beside it: twig.js renders the layout regardless, and the browser silently
   * fixes the markup a mail client would not.
   *
   * Building blocks are deliberately left out: a partial is a fragment of a
   * document (`<tr>` soup, half a table, a bare `<td>`), so the same checks
   * would report correct blocks as broken.
   */
  const layoutProblems = useMemo(
    () =>
      draftType === 'layout'
        ? validateLayoutSource(draftContent, { fragments: fragmentSources })
        : [],
    [draftType, draftContent, fragmentSources],
  );

  /**
   * The same check against the stored source, so a layout that *arrived* broken
   * can still be edited — see {@link blockingLayoutErrors}. Empty for a layout
   * being created: there is nothing to inherit, so every error is the author's.
   */
  const baselineProblems = useMemo(
    () =>
      draftType === 'layout' && record
        ? validateLayoutSource(record.content, { fragments: fragmentSources })
        : [],
    [draftType, record, fragmentSources],
  );

  const blockingProblems = useMemo(
    () => blockingLayoutErrors(layoutProblems, baselineProblems),
    [layoutProblems, baselineProblems],
  );

  const previewFragments = useMemo(
    () => buildPreviewFragments({ fragmentSources, draftKey, draftContent }),
    [fragmentSources, draftKey, draftContent],
  );

  const previewSource = useMemo(
    () =>
      buildFragmentPreviewSource({
        draftKey,
        fragmentType: draftType,
        previewLayoutKey: draftType === 'layout' ? null : previewLayoutKey,
        previewLayoutSource: previewLayoutKey
          ? fragmentSources[previewLayoutKey] ?? null
          : null,
        partialContent: draftContent,
      }),
    [draftKey, draftType, previewLayoutKey, fragmentSources, draftContent],
  );

  const freshBaseline = useMemo(
    () =>
      renderTwigPreview(previewSource, {
        fragments: previewFragments,
        translations: transMap,
        prefillSources: [draftContent],
      }),
    [previewSource, previewFragments, transMap, draftContent],
  );

  // Stable fillable rows: whatever placeholders/conditions survive a render
  // without sample values (same approach as the template previews). Held on the
  // last renderable draft, so a half-typed `{{` does not empty the panel.
  const baseline = useLastGoodRender(freshBaseline);

  const { placeholderPaths, conditionPaths } = useMemo(() => {
    return {
      placeholderPaths: baseline.placeholderVariables,
      conditionPaths: baseline.conditionVariables.filter(
        (path) => !baseline.placeholderVariables.includes(path),
      ),
    };
  }, [baseline]);

  /**
   * What the preview renders a fragment *with*.
   *
   * A partial is a shape with holes in it — `action_buttons` is nothing but
   * `{{ heading }}`, `{{ reschedule_text }}` and `{{ cancel_text }}` — so with no
   * values the preview was raw `{{ cancel_text }}` on a field of white space.
   * That reads as "the preview is broken" rather than "you have not filled
   * anything in", which is the same conclusion a reasonable person draws.
   *
   * So every placeholder the fragment exposes gets a readable stand-in derived
   * from its own name (`cancel_text` → "Cancel text"), and anything the user
   * types in the sample panel overrides it. The point of previewing a building
   * block is to see its *shape*; real copy is the template's job.
   *
   * Not applied to conditions: those decide whether a part appears at all, and
   * inventing a value would silently show a branch the fragment might not take.
   */
  /**
   * The fragment's own props, as the manifest sees them.
   *
   * `placeholderPaths` misses the two-stage form several partials use —
   * `{{ include(template_from_string(content))|raw }}` renders the *value* as a
   * template, and the renderer does not report `content` as an unresolved
   * variable. Seven listed partials preview completely blank for that reason
   * alone (`paragraph`, `small_header`, `big_header`, `checklist`,
   * `merge_button`, `small_section`, `xxxl/button`).
   *
   * `inferManifest` answers exactly the right question — "what props does this
   * partial take" — by reading its bare identifiers, so it covers both forms.
   */
  const inferredParams = useMemo(
    () =>
      inferManifest(draftKey, draftContent).params.map((param) => param.name),
    [draftKey, draftContent],
  );

  const draftManifest = useMemo(
    () => parseManifest(draftContent),
    [draftContent],
  );

  /**
   * Row labels for the preview panels. A variable the block declares as a field
   * shows the name editors see; everything else keeps its path form.
   */
  const variableLabel = useCallback(
    (path: string) => resolveVariableLabel(path, draftManifest?.params),
    [draftManifest],
  );

  /** Field names the block declares — the piece controls live only here. */
  const declaredParams = useMemo(
    () => (draftManifest?.params ?? []).map((param) => param.name),
    [draftManifest],
  );

  /**
   * Manifest problems in the draft, live from the buffer — the author sees a
   * degraded declaration *while typing it*, not after committing it.
   *
   * `literal-label` is dropped: it enforces a `core/*` policy (shipped chrome
   * must be translatable) that cannot apply to a partner's own blocks, which is
   * all this screen edits. Left in, naming a block "Call to action" raised a
   * warning banner about the author's own correct edit.
   */
  const manifestWarnings = useMemo(
    () =>
      (draftManifest?.warnings ?? []).filter(
        (warning) => warning.code !== MANIFEST_WARNING.LITERAL_LABEL,
      ),
    [draftManifest],
  );

  /** The Fields panel rewrites the whole buffer (comment plus markup). */
  const handleContentChange = useCallback(
    (content: string) => form.setFieldsValue({ content }),
    [form],
  );

  /**
   * The code editor's live selection, so *Make editable* can act on what is
   * highlighted. Held here rather than read on click: the button has to be able
   * to say it is unavailable before it is pressed.
   */
  const editorRef = useRef<TwigCodeEditorHandle>(null);
  const [selection, setSelection] = useState<EditorSelection | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);

  const handleSelectionChange = useCallback((next: EditorSelection) => {
    setSelection(next);
    // A refusal is about one selection; moving on retracts it.
    setSelectionError(null);
  }, []);

  const canMakeEditable = !!selection && selection.text.trim() !== '';

  /**
   * Turns the highlighted markup into something an editor can change.
   *
   * The whole design of the gesture is that it is safe to press: the
   * highlighted text stays on as the Twig `|default(…)`, so the block renders
   * exactly as it did until someone actually changes the value. Nobody has to
   * weigh "make this editable" against "keep the email as it is".
   */
  const handleMakeEditable = useCallback(() => {
    if (!selection) return;
    const manifest = draftManifest ?? inferManifest(draftKey, draftContent);
    const planned = planFieldFromSelection(
      draftContent,
      selection.from,
      selection.to,
      manifest.params.map((param) => param.name),
    );

    if (isRefusal(planned)) {
      setSelectionError(
        planned === 'contains-twig'
          ? getIntlTranslation(intl, {
              id: 'pages.emailCenter.editable.refuseTwig',
              defaultMessage:
                'That already contains a placeholder. Highlight plain text or an attribute value instead.',
            })
          : planned === 'inside-manifest'
          ? getIntlTranslation(intl, {
              id: 'pages.emailCenter.editable.refuseManifest',
              defaultMessage:
                'That is the settings comment at the top, not the block’s content.',
            })
          : getIntlTranslation(intl, {
              id: 'pages.emailCenter.editable.refuseEmpty',
              defaultMessage: 'Highlight something in the code first.',
            }),
      );
      return;
    }

    handleContentChange(
      withFieldFromSelection(
        draftContent,
        // A block with no declaration yet gets one built from what its markup
        // already reads, so promoting a selection cannot drop the rest.
        manifest.type === null ? manifest : { ...manifest, type: null },
        selection.from,
        selection.to,
        planned,
      ),
    );
    setSelectionError(null);
  }, [
    selection,
    draftManifest,
    draftKey,
    draftContent,
    intl,
    handleContentChange,
  ]);

  const makeEditableHint = canMakeEditable ? (
    <F
      id="pages.emailCenter.editable.makeHint"
      defaultMessage="Turn the highlighted text into something editors can change. The email keeps looking the same."
    />
  ) : (
    <F
      id="pages.emailCenter.editable.makeDisabled"
      defaultMessage="Highlight a piece of the code first — a headline, a link, a colour."
    />
  );

  const { theme, journeyHashId } = useJourneyTheme();
  const journeyEmailConfig = useSelector(
    EmailCenterSelectors.selectJourneyEmailConfig,
  );
  const variables = useSelector(EmailCenterSelectors.selectVariables);

  /** Namespaces the code editor offers as soon as `{{` is typed. */
  const variableGroups = useMemo(() => {
    const groups = variables
      ? Object.entries(variables).map(([namespace, items]) => ({
          namespace,
          items,
        }))
      : [];
    const colorKeys = Object.keys(theme?.colors ?? {});
    if (colorKeys.length > 0)
      groups.push({ namespace: 'theme.colors', items: colorKeys });
    const assetKeys = Object.keys(theme?.assets ?? {}).filter(
      (key) => typeof theme?.assets?.[key] === 'string',
    );
    if (assetKeys.length > 0)
      groups.push({ namespace: 'theme.assets', items: assetKeys });
    return groups;
  }, [variables, theme]);

  /**
   * Supplies `email_config.template_vars.*` — the logo, company name and imprint
   * the layout's own chrome reads. Without it the branded bands evaluate false
   * and the layout previews stripped of exactly the parts being designed.
   */
  useEffect(() => {
    if (!journeyHashId) return;
    dispatch({
      type: EMAIL_CENTER_ACTIONS.FETCH_JOURNEY_EMAIL_CONFIG,
      payload: { journeyHashId },
    });
  }, [journeyHashId, dispatch]);

  const previewContext = useMemo(() => {
    const merged: Record<string, string> = {};

    /**
     * Stand-ins so an unfilled block previews as its own shape rather than as a
     * field of white space. Declared fields as well as inferred ones: a field
     * only ever read through a `{% … %}` tag is invisible to inference, and
     * leaving it blank reads as a broken preview rather than an unfilled one.
     *
     * Named the way the panel names it, so the preview and the sample-data rows
     * agree about what each part is called.
     */
    for (const name of declaredParams) {
      merged[name] = variableLabel(name).label;
    }
    for (const name of inferredParams) {
      merged[name] = variableLabel(name).label;
    }
    for (const path of placeholderPaths) {
      merged[path] = variableLabel(path).label;
    }

    /**
     * The partner's real theme and email config, over the invented stand-ins.
     *
     * A fragment reading `{{ theme.colors.brand }}` or
     * `{{ email_config.template_vars.logo }}` is asking for something that
     * genuinely exists, so previewing it with the word "Brand" was worse than
     * useless — it made a correct fragment look wrong. These are the same values
     * the send uses, so the preview matches the email rather than approximating
     * it. Still below `testValues`: an explicit sample always wins.
     */
    Object.assign(
      merged,
      buildThemePreviewContext(theme),
      buildEmailConfigPreviewContext(journeyEmailConfig?.template_vars),
    );

    for (const [path, sample] of Object.entries(testValues)) {
      if (sample !== '') merged[path] = sample;
    }
    return merged;
  }, [
    declaredParams,
    inferredParams,
    placeholderPaths,
    variableLabel,
    theme,
    journeyEmailConfig,
    testValues,
  ]);

  const handleTestValueChange = useCallback((path: string, sample: string) => {
    setTestValues((current) => ({ ...current, [path]: sample }));
  }, []);

  const transKeys = useMemo(
    () => extractTransKeys(draftContent),
    [draftContent],
  );

  // Partner translations plus any unsaved edits — so the preview reflects
  // typed wording before it is persisted.
  const mergedTranslations = useMemo(() => {
    const merged = { ...transMap };
    for (const [key, sample] of Object.entries(transValues)) {
      if (sample !== '') merged[key] = sample;
    }
    return merged;
  }, [transMap, transValues]);

  const handleTransChange = useCallback((key: string, sample: string) => {
    setTransValues((current) => ({ ...current, [key]: sample }));
  }, []);

  // Persists the edited wording partner-wide for the default language; on
  // success the local override is dropped so the refreshed catalog value takes
  // over as the resolved text.
  const saveTranslation = useSaveTranslation();
  const handleTransSave = useCallback(
    (fullKey: string, value: string) => {
      setSavingTransKeys((current) => ({ ...current, [fullKey]: true }));
      saveTranslation(fullKey, { [LANGUAGE_CODES[0]]: value }).then((saved) => {
        setSavingTransKeys((current) => ({ ...current, [fullKey]: false }));
        // On success the refreshed catalog carries the new wording, so the local
        // override has to go or it would shadow it.
        if (!saved) return;
        setTransValues((current) => {
          const next = { ...current };
          delete next[fullKey];
          return next;
        });
      });
    },
    [saveTranslation],
  );

  const handleSave = useCallback(async () => {
    const values = await form.validateFields();
    setSaving(true);
    setGeneralError(null);

    const isUpdate = mode === 'edit' && record;
    const result = isUpdate
      ? await updateTemplateFragmentRequest({
          params: { fragmentId: record!.id },
          payload: {
            name: values.name,
            fragment_type: fragmentType,
            content: values.content,
          },
        })
      : await createTemplateFragmentRequest({
          payload: {
            name: values.name,
            fragment_type: fragmentType,
            content: values.content,
          },
        });
    setSaving(false);

    processAPIResponseNow(result, {
      successMessage: getIntlTranslation(intl, {
        id: 'pages.emailCenter.fragments.saved',
        defaultMessage: 'Fragment saved — emails render with it immediately.',
      }),
      onSuccess: () => {
        onSaved();
        onClose();
      },
      onError: (error) => {
        setGeneralError(normalizeApiError(error).message);
      },
    });
  }, [mode, record, fragmentType, form, intl, onSaved, onClose]);

  /**
   * Nothing in this drawer is written until Save, and it closes from five
   * places — Cancel, the header cross, the mask, Escape and the Back arrow of a
   * stacked drawer. A misplaced click on the mask threw the whole draft away
   * silently, so every one of them comes through here and an unsaved draft gets
   * asked about first.
   */
  const requestClose = useCallback(() => {
    const dirty = hasUnsavedFragmentChanges({
      draft: {
        // The watch is undefined for the first render, before the fields
        // register — the seed is what is on screen until then.
        name:
          typeof watchedName === 'string' ? watchedName : initialValues.name,
        content:
          typeof watchedContent === 'string'
            ? watchedContent
            : initialValues.content,
      },
      initial: {
        name: initialValues.name,
        content: initialValues.content,
      },
      translationEdits: transValues,
      savedTranslations: transMap,
    });

    if (!dirty) {
      onClose();
      return;
    }

    Modal.confirm({
      title: getIntlTranslation(intl, {
        id: 'pages.emailCenter.fragments.discardTitle',
        defaultMessage: 'Discard your changes?',
      }),
      content: getIntlTranslation(intl, {
        id: 'pages.emailCenter.fragments.discardConfirm',
        defaultMessage:
          'This block has edits that were never saved. Closing the editor loses them.',
      }),
      okText: getIntlTranslation(intl, {
        id: 'common.actions.discard',
        defaultMessage: 'Discard',
      }),
      okButtonProps: { danger: true },
      cancelText: getIntlTranslation(intl, {
        id: 'pages.emailCenter.fragments.keepEditing',
        defaultMessage: 'Keep editing',
      }),
      // Or the confirm opens behind the editor it is asking about.
      zIndex: zIndexAboveDrawers(),
      onOk: onClose,
    });
  }, [
    watchedName,
    watchedContent,
    initialValues,
    transValues,
    transMap,
    intl,
    onClose,
  ]);

  const canManageFragments = useHasAccess([PERMISSIONS.EmailFragments.MANAGE]);

  /**
   * Deleting from the editor — the moment you decide a block should go is while
   * you are looking at it, not back in the catalog.
   *
   * Only for a block the partner owns (`edit` is exactly that, see
   * `resolveFragmentDrawerMode`): a standard block has nothing to delete, and a
   * create or a copy has nothing yet. Refused when references would be left
   * dangling (`useFragmentDeletion`).
   */
  const { requestDelete, busyId, contextHolder } = useFragmentDeletion({
    // The catalog beneath is now wrong either way, and there is nothing left to
    // edit — close without the unsaved-changes prompt, the draft is the point.
    onDeleted: () => {
      onSaved();
      onClose();
    },
  });
  const canDelete = mode === 'edit' && !!record && canManageFragments;

  const namePlaceholder = getIntlTranslation(
    intl,
    draftType === 'layout'
      ? {
          id: 'pages.emailCenter.fragments.namePlaceholderLayout',
          defaultMessage: 'e.g. Newsletter layout',
        }
      : {
          id: 'pages.emailCenter.fragments.namePlaceholderPartial',
          defaultMessage: 'e.g. Winter header',
        },
  );

  // The type no longer has a field of its own, so the create title carries it.
  const titleText =
    mode === 'create' ? (
      draftType === 'layout' ? (
        <F
          id="pages.emailCenter.fragments.createLayout"
          defaultMessage="Create layout"
        />
      ) : (
        <F
          id="pages.emailCenter.fragments.createPartial"
          defaultMessage="Create building block"
        />
      )
    ) : isClone ? (
      <F
        id="pages.emailCenter.fragments.duplicateTitle"
        defaultMessage="Duplicate “{name}”"
        values={{ name: record ? fragmentDisplayName(record) : '' }}
      />
    ) : (
      <F
        id="pages.emailCenter.fragments.editTitle"
        defaultMessage="Edit “{name}”"
        values={{ name: record ? fragmentDisplayName(record) : '' }}
      />
    );

  const submitOnEnter = useSubmitOnEnter(handleSave, { enabled: !saving });

  return (
    <Drawer
      width={1100}
      open
      onOpenChange={requestClose}
      title={
        showBack ? (
          <Space size={8}>
            <Tooltip
              title={
                <F
                  id="pages.emailCenter.fragments.back"
                  defaultMessage="Back to the previous block"
                />
              }
            >
              <Button
                size="small"
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={requestClose}
              />
            </Tooltip>
            {titleText}
          </Space>
        ) : (
          titleText
        )
      }
      footer={
        <div className={styles.drawerFooter}>
          <Space>
            {canDelete && (
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={busyId === record!.id}
                onClick={() => requestDelete(record!)}
              >
                <F id="common.actions.delete" defaultMessage="Delete" />
              </Button>
            )}
          </Space>
          <Space>
            <Button onClick={requestClose}>
              <F id="common.actions.cancel" defaultMessage="Cancel" />
            </Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              <F id="common.actions.save" defaultMessage="Save" />
            </Button>
          </Space>
        </div>
      }
    >
      {generalError && (
        <Alert
          className={styles.generalError}
          type="error"
          showIcon
          message={generalError}
        />
      )}
      {/* Inside the tree, so the delete dialog gets the providers it reads. */}
      {contextHolder}
      <Form
        {...submitOnEnter}
        validateMessages={validateMessages}
        form={form}
        layout="vertical"
        initialValues={initialValues}
      >
        <Form.Item
          name="name"
          label={<F id="common.attribute.name" defaultMessage="Name" />}
          tooltip={
            <F
              id="pages.emailCenter.fragments.nameHint"
              defaultMessage="What this block is called in your Email Center. Rename it whenever you like — nothing references the name."
            />
          }
          rules={[{ required: true }, { max: 255 }]}
        >
          <Input placeholder={namePlaceholder} />
        </Form.Item>
        {/* The reference is shown, never offered as a field: it is assigned on
            save and immutable after, so an input would only invite an edit that
            cannot happen. It is still worth seeing — it is what a hand-written
            `{% include %}` has to name. */}
        {fragmentKey && (
          <div className={styles.referenceRow}>
            <span className={styles.referenceLabel}>
              <F
                id="pages.emailCenter.fragments.referenceLabel"
                defaultMessage="Reference"
              />
            </span>
            <Typography.Text code copyable={{ text: fragmentKey }}>
              {fragmentKey}
            </Typography.Text>
            <Tooltip
              title={
                <F
                  id="pages.emailCenter.fragments.referenceHint"
                  defaultMessage="How templates address this block ({example}). Assigned automatically and fixed for good, so existing references never break."
                  values={{
                    example: <code>{`{% include '${fragmentKey}' %}`}</code>,
                  }}
                />
              }
            >
              <InfoCircleOutlined className={styles.referenceInfo} />
            </Tooltip>
          </div>
        )}
        {unknownReferences.length > 0 && (
          <Alert
            className={styles.generalError}
            type="warning"
            showIcon
            message={
              <F
                id="pages.emailCenter.editor.unknownReferences"
                defaultMessage="No building block is registered under {names}. Emails using this template will fail to render until the block exists or the reference is corrected."
                values={{
                  names: <strong>{unknownReferences.join(', ')}</strong>,
                }}
              />
            }
          />
        )}
        {manifestWarnings.length > 0 && (
          <Alert
            className={styles.generalError}
            type="warning"
            showIcon
            message={
              <F
                id="pages.emailCenter.fragments.manifestWarnings"
                defaultMessage="Some of this block's settings could not be read, so a few may be missing below. The lines that caused it:"
              />
            }
            description={
              <ul className={styles.manifestWarningList}>
                {/* Parser output shown verbatim, like Twig errors — it names
                    the exact directive and line content. */}
                {manifestWarnings.map((warning) => (
                  <li key={warning.detail}>{warning.detail}</li>
                ))}
              </ul>
            }
          />
        )}
        {layoutProblems.length > 0 && (
          <Alert
            className={styles.generalError}
            type={blockingProblems.length > 0 ? 'error' : 'warning'}
            showIcon
            message={
              blockingProblems.length > 0 ? (
                <F
                  id="pages.emailCenter.layout.problemsBlocking"
                  defaultMessage="This layout cannot be saved yet — as it stands it would break every email that uses it."
                />
              ) : layoutErrors(layoutProblems).length > 0 ? (
                /* Errors the stored layout already had: reported, but not this
                   edit's fault, so they do not stand in the way of saving it. */
                <F
                  id="pages.emailCenter.layout.problemsInherited"
                  defaultMessage="This layout already had problems before this edit. Saving still works, but emails using it are affected."
                />
              ) : (
                <F
                  id="pages.emailCenter.layout.problemsAdvisory"
                  defaultMessage="Worth fixing in this layout:"
                />
              )
            }
            description={
              <ul className={styles.manifestWarningList}>
                {layoutProblems.map((problem) => {
                  const message = layoutProblemMessage(problem.code);
                  return (
                    <li key={`${problem.code}:${problem.detail}`}>
                      {message ? (
                        <F
                          id={message.id}
                          defaultMessage={message.defaultMessage}
                        />
                      ) : (
                        /* A code with no copy yet is better shown verbatim than
                           mislabelled — same rule as the preflight issues. */
                        problem.detail
                      )}{' '}
                      {problem.at === undefined ? (
                        message && (
                          <Typography.Text
                            type="secondary"
                            className={styles.layoutProblemDetail}
                          >
                            {problem.detail}
                          </Typography.Text>
                        )
                      ) : (
                        // The detail names the tag and the line, so it doubles
                        // as the way to get to it.
                        <Button
                          type="link"
                          size="small"
                          className={styles.layoutProblemDetail}
                          onClick={() =>
                            editorRef.current?.reveal(problem.at!, problem.at!)
                          }
                        >
                          {problem.detail}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            }
          />
        )}
        <div className={styles.editorPreviewRow}>
          <div className={styles.editorCol}>
            <div className={styles.codeHeader}>
              <Typography.Text strong>
                <F
                  id="pages.emailCenter.fragments.content"
                  defaultMessage="Content (HTML)"
                />
              </Typography.Text>
              {/* A layout wraps the whole template rather than being a block
                  editors place, so nothing in one is theirs to change. */}
              {draftType !== 'layout' && (
                <Tooltip title={makeEditableHint}>
                  <Button
                    size="small"
                    icon={<HighlightOutlined />}
                    disabled={!canMakeEditable}
                    onClick={handleMakeEditable}
                  >
                    <F
                      id="pages.emailCenter.editable.make"
                      defaultMessage="Make editable"
                    />
                  </Button>
                </Tooltip>
              )}
            </div>
            {selectionError && (
              <Typography.Paragraph type="danger" className={styles.codeError}>
                {selectionError}
              </Typography.Paragraph>
            )}
            <Form.Item
              name="content"
              rules={[
                { required: true },
                /**
                 * The gate. Everything above is advisory until here: a layout
                 * that would arrive empty, or whose markup does not nest, is not
                 * saveable — the alert says which, so this only has to stop it.
                 */
                {
                  validator: () =>
                    blockingProblems.length === 0
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            getIntlTranslation(intl, {
                              id: 'pages.emailCenter.layout.fixBeforeSaving',
                              defaultMessage:
                                'Fix the problems listed above before saving this layout.',
                            }),
                          ),
                        ),
                },
              ]}
            >
              <TwigCodeEditor
                ref={editorRef}
                minHeight="220px"
                maxHeight="480px"
                variableGroups={variableGroups}
                onOpenReference={onOpenReference}
                onSelectionChange={handleSelectionChange}
              />
            </Form.Item>
            {draftType !== 'layout' && (
              <EditableContentPanel
                content={draftContent}
                fragmentKey={draftKey}
                onChange={handleContentChange}
                onReveal={(from, to) => editorRef.current?.reveal(from, to)}
              />
            )}
          </div>
          <div className={styles.previewCol}>
            <div className={styles.previewHeader}>
              <Typography.Text strong>
                <F
                  id="pages.emailCenter.fragments.preview"
                  defaultMessage="Live preview"
                />
              </Typography.Text>
              {draftType !== 'layout' && (
                <Select
                  size="small"
                  className={styles.previewLayoutSelect}
                  allowClear
                  showSearch
                  value={previewLayoutKey}
                  onChange={(value) => setPreviewLayoutKey(value ?? null)}
                  options={layoutChoices}
                  filterOption={(input, option) =>
                    `${option?.label ?? ''} ${option?.value ?? ''}`
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder={getIntlTranslation(intl, {
                    id: 'pages.emailCenter.fragments.previewLayout',
                    defaultMessage: 'Show inside layout…',
                  })}
                />
              )}
            </div>
            <PreviewPane
              html={previewSource}
              fragments={previewFragments}
              previewContext={previewContext}
              translations={mergedTranslations}
              prefillSources={[draftContent]}
            />
            <TestDataPanel
              className={styles.samplePanel}
              sections={[
                {
                  title: (
                    <F
                      id="pages.emailCenter.editor.testData"
                      defaultMessage="Sample data"
                    />
                  ),
                  hint: (
                    <F
                      id="pages.emailCenter.editor.testDataHint"
                      defaultMessage="Example values for this preview only — nothing is saved."
                    />
                  ),
                  defaultCollapsed: true,
                  rows: placeholderPaths.map((path) => ({
                    key: path,
                    ...variableLabel(path),
                    value: testValues[path] ?? '',
                  })),
                  onChange: handleTestValueChange,
                },
                {
                  title: (
                    <F
                      id="pages.emailCenter.editor.conditions"
                      defaultMessage="Optional content"
                    />
                  ),
                  hint: (
                    <F
                      id="pages.emailCenter.editor.conditionsHint"
                      defaultMessage="These values decide which parts show. Enter a value to try a part — 'false' or '0' hides it."
                    />
                  ),
                  defaultCollapsed: true,
                  rows: conditionPaths.map((path) => ({
                    key: path,
                    ...variableLabel(path),
                    value: testValues[path] ?? '',
                  })),
                  onChange: handleTestValueChange,
                },
                {
                  title: (
                    <F
                      id="pages.emailCenter.preview.translations"
                      defaultMessage="Email texts"
                    />
                  ),
                  hint: (
                    <F
                      id="pages.emailCenter.preview.translationsHint"
                      defaultMessage="Your saved wording. Type a new text and press ✓ to use it in all emails."
                    />
                  ),
                  // Expand when a used text has no saved value yet, so gaps
                  // (e.g. standard blocks referencing global-only keys) are
                  // visible; otherwise keep it out of the way.
                  defaultCollapsed: transKeys.every(
                    (key) => transMap[key] !== undefined,
                  ),
                  rows: transKeys.map((key) => ({
                    key,
                    ...humanizeTransKey(key),
                    value: transValues[key] ?? '',
                    placeholder: transMap[key],
                    missing: transMap[key] === undefined,
                    saveable: !!transValues[key],
                    saving: !!savingTransKeys[key],
                  })),
                  onChange: handleTransChange,
                  onSave: handleTransSave,
                },
              ]}
            />
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export default FragmentEditorDrawer;
